import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

export default function Footer() {
  const { t } = useT();
  const servicesList = t("footer.servicesList");

  const links = [
    { label: t("nav.about"), id: "about" },
    { label: t("nav.services"), id: "services" },
    { label: t("nav.process"), id: "process" },
    { label: t("nav.contact"), id: "contact" },
  ];

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid="main-footer" className="relative text-white mt-10 overflow-hidden" style={{ background: "linear-gradient(135deg, #0F0524 0%, #2A1458 100%)" }}>
      <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, #00F0FF 0%, transparent 70%)" }} />
      <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, #FF00E5 0%, transparent 70%)" }} />
      <div className="absolute inset-0 ambient-grid opacity-10" />

      <div className="relative border-b border-white/10 py-10 overflow-hidden" dir="ltr">
        <div className="marquee-track flex whitespace-nowrap gap-12 font-display text-4xl md:text-6xl font-extralight tracking-tight">
          {Array.from({ length: 2 }).map((_, row) => (
            <div key={row} className="flex gap-12 items-center shrink-0">
              {Array.from({ length: 6 }).map((__, i) => (
                <span key={i} className="flex items-center gap-12">
                  <span className="grad-text italic">Power in Intelligence</span>
                  <span className="text-white/40">✦</span>
                  <span>Strength in Innovation</span>
                  <span className="text-white/40">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="relative container mx-auto px-6 md:px-12 max-w-7xl py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 pb-14 border-b border-white/10">
          <div className="md:col-span-5 space-y-6">
            <div className="flex items-center gap-3">
              <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-2xl overflow-hidden bg-white shadow-[0_4px_16px_rgba(0,0,0,0.15)]">
                <img
                  src="/logo.jpg"
                  alt="MARCHUK LLC"
                  className="h-full w-full object-contain p-1.5"
                  loading="lazy"
                />
              </span>
              <div className="font-display text-2xl font-semibold tracking-tight">
                MARCHUK <span className="font-light opacity-60">LLC</span>
              </div>
            </div>
            <p className="text-[15px] leading-relaxed text-white/70 max-w-sm">
              <span className="grad-text font-semibold">{t("footer.about")}</span> {t("footer.aboutSub")}
            </p>
            <a href="mailto:lionsoftware@mmaarchuuk.com" data-testid="footer-email" className="inline-flex items-center gap-3 glass-dark rounded-full px-4 py-3 text-[13px] font-medium">
              <Mail className="h-4 w-4 text-[#A49BFF]" />
              lionsoftware@mmaarchuuk.com
              <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
            </a>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-semibold mb-5">{t("footer.quickLinks")}</h4>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.id}>
                  <button onClick={() => go(l.id)} className="ink-link text-[14px] text-white/80 hover:text-white">
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-semibold mb-5">{t("footer.services")}</h4>
            <ul className="space-y-3">
              {servicesList.map((s) => (
                <li key={s}>
                  <button onClick={() => go("services")} className="ink-link text-[14px] text-white/80 hover:text-white text-left rtl:text-right">
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-semibold mb-5">{t("footer.contactUs")}</h4>
            <ul className="space-y-4">
              <li>
                <div className="text-[11px] text-white/50 uppercase tracking-wider">{t("footer.phone")}</div>
                <a href="tel:045771388" data-testid="footer-phone" className="mt-1 inline-flex items-center gap-2 text-[14px] text-white/90 hover:text-white" dir="ltr">
                  <Phone className="h-3.5 w-3.5 text-[#A49BFF]" />
                  (04) 577 1388
                </a>
              </li>
              <li>
                <div className="text-[11px] text-white/50 uppercase tracking-wider">{t("footer.address")}</div>
                <a href="https://maps.google.com/?q=Tamani+Arts+Building+Al+Asayel+Street+Business+Bay+Dubai" target="_blank" rel="noopener noreferrer" data-testid="footer-address" className="mt-1 inline-flex items-start gap-2 text-[13px] text-white/90 hover:text-white leading-snug">
                  <MapPin className="h-3.5 w-3.5 text-[#A49BFF] mt-0.5 shrink-0" />
                  <span>
                    {t("contact.addressLine1")}
                    <br />
                    {t("contact.addressLine2")}
                  </span>
                </a>
              </li>
              <li>
                <div className="text-[11px] text-white/50 uppercase tracking-wider">{t("footer.hours")}</div>
                <div className="mt-1 text-[14px] text-white/90">{t("footer.hoursValue")}</div>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-white/50">
          <div>&copy; 2026 MARCHUK LLC. {t("footer.rights")}</div>
          <div className="flex items-center gap-6">
            <span>{t("footer.privacy")}</span>
            <span>{t("footer.terms")}</span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full iridescent" />
              {t("footer.systemActive")}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
